import { SurveyV2 } from '@act/shared/data-transfer-objects';
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { Control, Controller, Path } from 'react-hook-form';
import type { SurveyFormData } from './types';

export interface CheckboxInputProps {
  control: Control<SurveyFormData>;
  name: string;
  options: SurveyV2.OptionDto[];
  isRequired: boolean;
}

/**
 * Multi-select checkbox options for a survey question, bound to react-hook-form.
 *
 * @param props - Form control, field name, options, and required flag
 * @returns Checkbox group wired to `name` as `number[]` option ids
 */
const CheckboxInput = ({ control, name, options }: CheckboxInputProps) => {
  return (
    <Controller
      control={control}
      name={name as Path<SurveyFormData>}
      defaultValue={[]}
      render={({ field: { onChange, value } }) => {
        const currentValue = (
          Array.isArray(value) ? value.map(Number) : []
        ) as number[];

        /**
         * Toggles an option id in the selected array.
         *
         * @param optionId - Option primary key
         */
        const handleChange = (optionId: number) => {
          const id = Number(optionId);
          const newValue = currentValue.includes(id)
            ? currentValue.filter(selectedId => selectedId !== id)
            : [...currentValue, id];
          onChange(newValue);
        };

        return (
          <FormGroup>
            {options.map(option => {
              const optionId = Number(option.id);
              return (
                <FormControlLabel
                  key={optionId}
                  control={
                    <Checkbox
                      checked={currentValue.includes(optionId)}
                      onChange={() => handleChange(optionId)}
                    />
                  }
                  label={option.text}
                />
              );
            })}
          </FormGroup>
        );
      }}
    />
  );
};

export default CheckboxInput;
